import { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, Edit2, MoreVertical, Search, Workflow, Filter, ArrowRight, CheckSquare, Square, Clock, AlertCircle } from "lucide-react";
import api from "../../services/api";
import Filters from "./Filters";

export default function WorkflowManagement({ currentUserRole, roles, onClose }) {
  const [workflows, setWorkflows] = useState([]);
  const [showWorkflowForm, setShowWorkflowForm] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [showStepConfig, setShowStepConfig] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [searchWorkflows, setSearchWorkflows] = useState("");
  const [openWorkflowMenuId, setOpenWorkflowMenuId] = useState(null);
  const workflowMenuRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterResults, setFilterResults] = useState(null);
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1); // 1: Select Module/Trigger, 2: Basic Info, 3: Steps Configuration, 4: Review
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedTrigger, setSelectedTrigger] = useState("");
  const [workflowFormData, setWorkflowFormData] = useState({
    workflowName: "",
    workflowCode: "",
    moduleName: "",
    workflowCategory: "",
    triggerEvent: "",
    applicableEmployeeScope: "all",
    workflowActive: true,
  });

  // Predefined module-wise triggers
  const moduleTriggers = {
    "Leave": [
      { value: "leave_application", label: "Leave Application" },
      { value: "leave_cancellation", label: "Leave Cancellation" },
      { value: "leave_extension", label: "Leave Extension" },
    ],
    "Payroll": [
      { value: "salary_revision", label: "Salary Revision" },
      { value: "variable_pay", label: "Variable Pay" },
      { value: "payroll_hold_release", label: "Payroll Hold/Release" },
      { value: "bonus", label: "Bonus" },
      { value: "reimbursement", label: "Reimbursement" },
    ],
    "Employee": [
      { value: "master_data_change", label: "Master Data Change" },
      { value: "investment_declaration", label: "Investment Declaration" },
      { value: "proof_of_investment", label: "Proof of Investment" },
      { value: "nomination_update", label: "Nomination Update" },
      { value: "resignation", label: "Resignation" },
      { value: "shift_change", label: "Shift Change" },
      { value: "work_from_home", label: "Work from Home" },
    ],
    "HR": [
      { value: "recruitment_requisition", label: "Recruitment Requisition" },
      { value: "offer_approval", label: "Offer Approval" },
      { value: "pre_boarding", label: "Pre-boarding" },
      { value: "onboarding", label: "Onboarding" },
      { value: "probation_confirmation", label: "Probation Confirmation" },
      { value: "promotion", label: "Promotion" },
      { value: "policy_acknowledgement", label: "Policy Acknowledgement" },
      { value: "posh_complaint", label: "POSH Complaint" },
    ],
    "Attendance": [
      { value: "attendance_regularization", label: "Attendance Regularization" },
      { value: "overtime", label: "Overtime" },
      { value: "comp_off", label: "Comp-off" },
    ],
    "Helpdesk": [
      { value: "ticket_handling", label: "Ticket Handling" },
      { value: "it_asset_request", label: "IT Asset Request" },
      { value: "access_provisioning", label: "Access Provisioning" },
    ],
    "Admin": [
      { value: "role_change", label: "Role Change" },
      { value: "master_data_change", label: "Master Data Change" },
      { value: "branch_creation", label: "Branch Creation" },
      { value: "admin_promotion", label: "Admin Promotion" },
    ],
    "Compliance": [
      { value: "compliance_submission", label: "Compliance Submission" },
      { value: "compliance_review", label: "Compliance Review" },
    ],
    "Assets": [
      { value: "asset_request", label: "Asset Request" },
      { value: "asset_return", label: "Asset Return" },
    ],
    "Recruitment": [
      { value: "recruitment_requisition", label: "Recruitment Requisition" },
      { value: "offer_approval", label: "Offer Approval" },
    ],
    "Onboarding": [
      { value: "pre_boarding", label: "Pre-boarding" },
      { value: "onboarding", label: "Onboarding" },
    ],
    "Investment": [
      { value: "investment_declaration", label: "Investment Declaration" },
      { value: "proof_of_investment", label: "Proof of Investment" },
    ],
    "Reimbursement": [
      { value: "reimbursement", label: "Reimbursement" },
    ],
  };

  const moduleOptions = Object.keys(moduleTriggers);
  const categoryOptions = ["HR", "Payroll", "Accounts", "IT", "Admin", "Employee"];

  useEffect(() => {
    fetchWorkflows();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workflowMenuRef.current && !workflowMenuRef.current.contains(event.target)) {
        setOpenWorkflowMenuId(null);
      }
    };

    if (openWorkflowMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openWorkflowMenuId]);

  const fetchWorkflows = async () => {
    try {
      const response = await api.get("/global-settings/workflows");
      if (response.data.success) {
        setWorkflows(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching workflows:", error);
      const stored = localStorage.getItem("workflows");
      if (stored) {
        setWorkflows(JSON.parse(stored));
      } else {
        setWorkflows([]);
      }
    }
  };

  const handleWorkflowSubmit = async (e) => {
    e.preventDefault();
    
    if (wizardStep < 4) {
      // Move to next step
      if (wizardStep === 1) {
        // Validate module and trigger selection
        if (!selectedModule || !selectedTrigger) {
          alert("Please select both Module and Trigger Event");
          return;
        }
        setWorkflowFormData({
          ...workflowFormData,
          moduleName: selectedModule,
          triggerEvent: selectedTrigger,
        });
        setWizardStep(2);
      } else if (wizardStep === 2) {
        // Validate basic info
        if (!workflowFormData.workflowName || !workflowFormData.workflowCategory) {
          alert("Please fill in all required fields");
          return;
        }
        // Validate filter if selected
        if (workflowFormData.applicableEmployeeScope === "filter" && !filterResults) {
          alert("Please apply a filter before proceeding.");
          setShowFilters(true);
          return;
        }
        setWizardStep(3);
      } else if (wizardStep === 3) {
        // Validate steps
        if (workflowSteps.length === 0) {
          alert("Please add at least one workflow step");
          return;
        }
        setWizardStep(4);
      }
      return;
    }

    // Step 4: Save workflow
    try {
      const payload = {
        name: workflowFormData.workflowName,
        code: workflowFormData.workflowCode || workflowFormData.workflowName.toUpperCase().replace(/\s+/g, "_"),
        moduleName: workflowFormData.moduleName,
        category: workflowFormData.workflowCategory,
        triggerEvent: workflowFormData.triggerEvent,
        applicableEmployeeScope: workflowFormData.applicableEmployeeScope,
        active: workflowFormData.workflowActive,
        steps: workflowSteps,
        filterData: workflowFormData.applicableEmployeeScope === "filter" ? filterResults : null,
      };

      try {
        if (editingWorkflow) {
          await api.put(`/global-settings/workflows/${editingWorkflow.id}`, payload);
        } else {
          await api.post("/global-settings/workflows", payload);
        }
      } catch (apiError) {
        const stored = localStorage.getItem("workflows");
        const currentList = stored ? JSON.parse(stored) : [];
        
        const workflowData = {
          id: editingWorkflow ? editingWorkflow.id : Date.now().toString(),
          ...payload,
          createdAt: editingWorkflow ? editingWorkflow.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        if (editingWorkflow) {
          const updatedList = currentList.map(w => w.id === editingWorkflow.id ? workflowData : w);
          localStorage.setItem("workflows", JSON.stringify(updatedList));
        } else {
          currentList.push(workflowData);
          localStorage.setItem("workflows", JSON.stringify(currentList));
        }
      }

      await fetchWorkflows();
      resetWorkflowWizard();
      alert(editingWorkflow ? "Workflow updated successfully!" : "Workflow created successfully!");
    } catch (error) {
      console.error("Error saving workflow:", error);
      alert("Failed to save workflow. Please try again.");
    }
  };

  const resetWorkflowWizard = () => {
    setWizardStep(1);
    setSelectedModule("");
    setSelectedTrigger("");
    setWorkflowFormData({
      workflowName: "",
      workflowCode: "",
      moduleName: "",
      workflowCategory: "",
      triggerEvent: "",
      applicableEmployeeScope: "all",
      workflowActive: true,
    });
    setWorkflowSteps([]);
    setFilterResults(null);
    setEditingWorkflow(null);
    setShowWorkflowForm(false);
  };

  const handleEditWorkflow = (workflow) => {
    setEditingWorkflow(workflow);
    setSelectedModule(workflow.moduleName || "");
    setSelectedTrigger(workflow.triggerEvent || "");
    setWorkflowFormData({
      workflowName: workflow.name || "",
      workflowCode: workflow.code || "",
      moduleName: workflow.moduleName || "",
      workflowCategory: workflow.category || "",
      triggerEvent: workflow.triggerEvent || "",
      applicableEmployeeScope: workflow.applicableEmployeeScope || "all",
      workflowActive: workflow.active !== undefined ? workflow.active : true,
    });
    setWorkflowSteps(workflow.steps || []);
    setFilterResults(workflow.filterData || null);
    setWizardStep(1);
    setShowWorkflowForm(true);
  };

  const handleDeleteWorkflow = async (id) => {
    if (!window.confirm("Are you sure you want to delete this workflow?")) return;
    try {
      try {
        await api.delete(`/global-settings/workflows/${id}`);
      } catch (apiError) {
        const stored = localStorage.getItem("workflows");
        if (stored) {
          const currentList = JSON.parse(stored);
          const updatedList = currentList.filter(w => w.id !== id);
          localStorage.setItem("workflows", JSON.stringify(updatedList));
        }
      }
      await fetchWorkflows();
      alert("Workflow deleted successfully!");
    } catch (error) {
      console.error("Error deleting workflow:", error);
      alert("Failed to delete workflow. Please try again.");
    }
  };

  const handleAddStep = () => {
    const newStep = {
      stepNumber: workflowSteps.length + 1,
      stepName: "",
      stepType: "approval", // "approval" or "notify"
      approverType: "role",
      approverRole: null,
      approverEmployee: null,
      actions: {
        approve: true,
        reject: true,
        sendBack: false,
        autoApprove: false,
      },
      sla: {
        enabled: false,
        hours: 24,
      },
      escalation: {
        enabled: false,
        escalateTo: null,
      },
      conditions: [],
      parallelApproval: false,
    };
    setWorkflowSteps([...workflowSteps, newStep]);
    setEditingStepIndex(workflowSteps.length);
    setShowStepConfig(true);
  };

  const handleEditStep = (index) => {
    setEditingStepIndex(index);
    setShowStepConfig(true);
  };

  const handleDeleteStep = (index) => {
    const updatedSteps = workflowSteps.filter((_, i) => i !== index).map((step, i) => ({
      ...step,
      stepNumber: i + 1,
    }));
    setWorkflowSteps(updatedSteps);
  };

  const handleSaveStep = (stepData) => {
    if (editingStepIndex !== null) {
      const updatedSteps = [...workflowSteps];
      updatedSteps[editingStepIndex] = { ...stepData, stepNumber: editingStepIndex + 1 };
      setWorkflowSteps(updatedSteps);
    }
    setShowStepConfig(false);
    setEditingStepIndex(null);
  };

  const handleFilterApply = (employeesData, filters, params) => {
    setFilterResults({ employeesData, filters, params });
    setShowFilters(false);
  };

  const handleFilterClose = () => {
    setShowFilters(false);
  };

  const renderWizardStep1 = () => {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-bold mb-4">Select Module and Trigger Event</h3>
          <p className="text-sm text-gray-600 mb-4">Choose the module and predefined trigger event for this workflow</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Module Name *</label>
            <select
              value={selectedModule}
              onChange={(e) => {
                setSelectedModule(e.target.value);
                setSelectedTrigger(""); // Reset trigger when module changes
              }}
              className="w-full px-3 py-2 border border-gray-300"
              required
            >
              <option value="">Select Module</option>
              {moduleOptions.map(module => (
                <option key={module} value={module}>{module}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Trigger Event *</label>
            <select
              value={selectedTrigger}
              onChange={(e) => setSelectedTrigger(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300"
              required
              disabled={!selectedModule}
            >
              <option value="">Select Trigger Event</option>
              {selectedModule && moduleTriggers[selectedModule] ? (
                moduleTriggers[selectedModule].map(trigger => (
                  <option key={trigger.value} value={trigger.value}>{trigger.label}</option>
                ))
              ) : (
                <option value="">Select Module first</option>
              )}
            </select>
          </div>
        </div>
        {selectedModule && selectedTrigger && (
          <div className="mt-4 p-3 border border-redhalo-teal/50 bg-redhalo-mint/40">
            <p className="text-sm text-redhalo-navy">
              <strong>Selected:</strong> {selectedModule} - {moduleTriggers[selectedModule]?.find(t => t.value === selectedTrigger)?.label || selectedTrigger}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderWizardStep2 = () => {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-bold mb-4">Workflow Basic Information</h3>
          <p className="text-sm text-gray-600 mb-4">Provide basic details for the workflow</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Workflow Name *</label>
            <input
              type="text"
              value={workflowFormData.workflowName}
              onChange={(e) => setWorkflowFormData({ ...workflowFormData, workflowName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300"
              required
              placeholder="e.g., Leave Approval Workflow"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Workflow Code</label>
            <input
              type="text"
              value={workflowFormData.workflowCode}
              onChange={(e) => setWorkflowFormData({ ...workflowFormData, workflowCode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300"
              placeholder="Auto-generated if empty"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Workflow Category *</label>
            <select
              value={workflowFormData.workflowCategory}
              onChange={(e) => setWorkflowFormData({ ...workflowFormData, workflowCategory: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300"
              required
            >
              <option value="">Select Category</option>
              {categoryOptions.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Applicable Employee Scope *</label>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="applicableEmployeeScope"
                  value="all"
                  checked={workflowFormData.applicableEmployeeScope === "all"}
                  onChange={(e) => {
                    setWorkflowFormData({ ...workflowFormData, applicableEmployeeScope: e.target.value });
                    setFilterResults(null);
                  }}
                  className="w-4 h-4"
                />
                <span className="text-sm">All Employees</span>
              </label>
              <div className="flex items-center space-x-2">
                <label className="flex items-center space-x-2 cursor-pointer flex-1">
                  <input
                    type="radio"
                    name="applicableEmployeeScope"
                    value="filter"
                    checked={workflowFormData.applicableEmployeeScope === "filter"}
                    onChange={(e) => {
                      setWorkflowFormData({ ...workflowFormData, applicableEmployeeScope: e.target.value });
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Use Filter</span>
                </label>
                {workflowFormData.applicableEmployeeScope === "filter" && (
                  <button
                    type="button"
                    onClick={() => setShowFilters(true)}
                    className="flex items-center space-x-1 rh-btn-primary text-sm px-3 py-1.5"
                  >
                    <Filter className="w-4 h-4" />
                    <span>Go</span>
                  </button>
                )}
              </div>
              {workflowFormData.applicableEmployeeScope === "filter" && filterResults && (
                <div className="ml-6 mt-2 p-2 border border-gray-200">
                  <p className="text-xs text-gray-600">
                    {filterResults.employeesData?.length || 0} employee(s) selected
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowFilters(true)}
                    className="text-xs text-redhalo-navy hover:text-redhalo-red mt-1"
                  >
                    Change Filter
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={workflowFormData.workflowActive}
                onChange={(e) => setWorkflowFormData({ ...workflowFormData, workflowActive: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">Active</span>
            </label>
          </div>
        </div>
      </div>
    );
  };

  const renderWizardStep3 = () => {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-bold mb-4">Configure Workflow Steps</h3>
          <p className="text-sm text-gray-600 mb-4">Add and configure approval steps for this workflow</p>
        </div>
        <div className="flex justify-end mb-3">
          <button
            type="button"
            onClick={handleAddStep}
            className="flex items-center space-x-1 rh-btn-primary text-sm px-3 py-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>Add Step</span>
          </button>
        </div>
        {workflowSteps.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8 border border-gray-200">
            No steps configured. Click "Add Step" to configure workflow steps.
          </p>
        ) : (
          <div className="space-y-2">
            {workflowSteps.map((step, index) => (
              <div key={index} className="border border-gray-300 p-3">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">Step {step.stepNumber}</span>
                      {step.stepName && (
                        <span className="text-sm text-gray-600">- {step.stepName}</span>
                      )}
                      <span className={`px-2 py-0.5 text-[10px] font-medium ${
                        step.stepType === "notify" ? "bg-yellow-100 text-yellow-800" : "rh-accent-soft"
                      }`}>
                        {step.stepType === "notify" ? "Notify Only" : "Approval Required"}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-[10px] font-medium">
                        {step.approverType === "role" ? `Role: ${step.approverRole || "N/A"}` :
                         step.approverType === "reporting_manager" ? "Reporting Manager" :
                         step.approverType === "skip_if_self" ? "Skip if Self" :
                         step.approverType === "individual" ? `Employee: ${step.approverEmployee || "N/A"}` : "N/A"}
                      </span>
                    </div>
                    {step.stepType === "approval" && (
                      <div className="text-xs text-gray-500">
                        Actions: {Object.entries(step.actions || {}).filter(([_, enabled]) => enabled).map(([action]) => action).join(", ") || "None"}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditStep(index)}
                      className="text-redhalo-navy hover:text-redhalo-red"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteStep(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderWizardStep4 = () => {
    const selectedTriggerLabel = moduleTriggers[selectedModule]?.find(t => t.value === selectedTrigger)?.label || selectedTrigger;
    
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-bold mb-4">Review Workflow Configuration</h3>
          <p className="text-sm text-gray-600 mb-4">Review all details before saving the workflow</p>
        </div>
        <div className="border border-gray-300 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Workflow Name</label>
              <p className="text-sm font-semibold">{workflowFormData.workflowName}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Workflow Code</label>
              <p className="text-sm">{workflowFormData.workflowCode || "Auto-generated"}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Module</label>
              <p className="text-sm">{selectedModule}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Trigger Event</label>
              <p className="text-sm">{selectedTriggerLabel}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Category</label>
              <p className="text-sm">{workflowFormData.workflowCategory}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Employee Scope</label>
              <p className="text-sm">
                {workflowFormData.applicableEmployeeScope === "all" 
                  ? "All Employees" 
                  : `Filtered (${filterResults?.employeesData?.length || 0} employees)`}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Status</label>
              <p className="text-sm">{workflowFormData.workflowActive ? "Active" : "Inactive"}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Total Steps</label>
              <p className="text-sm">{workflowSteps.length} step(s)</p>
            </div>
          </div>
          {workflowSteps.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-300">
              <label className="text-xs font-medium text-gray-500 mb-2 block">Workflow Steps</label>
              <div className="space-y-2">
                {workflowSteps.map((step, index) => (
                  <div key={index} className="p-2 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">Step {step.stepNumber}:</span>
                      <span className="text-xs">{step.stepName || "Unnamed Step"}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-medium ${
                        step.stepType === "notify" ? "bg-yellow-100 text-yellow-800" : "rh-accent-soft"
                      }`}>
                        {step.stepType === "notify" ? "Notify" : "Approval"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWorkflowStepConfig = () => {
    if (!showStepConfig || editingStepIndex === null) return null;

    const currentStep = workflowSteps[editingStepIndex] || {
      stepNumber: editingStepIndex + 1,
      stepName: "",
      stepType: "approval",
      approverType: "role",
      approverRole: null,
      approverEmployee: null,
      actions: { approve: true, reject: true, sendBack: false, autoApprove: false },
      sla: { enabled: false, hours: 24 },
      escalation: { enabled: false, escalateTo: null },
      conditions: [],
      parallelApproval: false,
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[70]">
        <div className="bg-white shadow-xl relative w-[900px] h-[800px] flex flex-col border border-gray-300">
          <div className="flex items-center justify-between p-4 border-b border-gray-300 flex-shrink-0">
            <h3 className="text-xl font-bold">Configure Step {currentStep.stepNumber}</h3>
            <button
              onClick={() => {
                setShowStepConfig(false);
                setEditingStepIndex(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Step Type *</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`stepType_${editingStepIndex}`}
                      value="approval"
                      checked={currentStep.stepType === "approval"}
                      onChange={(e) => {
                        const updatedStep = { ...currentStep, stepType: e.target.value };
                        const updatedSteps = [...workflowSteps];
                        updatedSteps[editingStepIndex] = updatedStep;
                        setWorkflowSteps(updatedSteps);
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Approval Required (Existing Functionality)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`stepType_${editingStepIndex}`}
                      value="notify"
                      checked={currentStep.stepType === "notify"}
                      onChange={(e) => {
                        const updatedStep = { ...currentStep, stepType: e.target.value };
                        const updatedSteps = [...workflowSteps];
                        updatedSteps[editingStepIndex] = updatedStep;
                        setWorkflowSteps(updatedSteps);
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Notify Only</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Step Name</label>
                <input
                  type="text"
                  value={currentStep.stepName || ""}
                  onChange={(e) => {
                    const updatedStep = { ...currentStep, stepName: e.target.value };
                    const updatedSteps = [...workflowSteps];
                    updatedSteps[editingStepIndex] = updatedStep;
                    setWorkflowSteps(updatedSteps);
                  }}
                  className="w-full px-3 py-2 border border-gray-300"
                  placeholder="e.g., Manager Approval, HR Review"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Approver Type *</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`approverType_${editingStepIndex}`}
                      value="role"
                      checked={currentStep.approverType === "role"}
                      onChange={(e) => {
                        const updatedStep = { ...currentStep, approverType: e.target.value };
                        const updatedSteps = [...workflowSteps];
                        updatedSteps[editingStepIndex] = updatedStep;
                        setWorkflowSteps(updatedSteps);
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Role</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`approverType_${editingStepIndex}`}
                      value="reporting_manager"
                      checked={currentStep.approverType === "reporting_manager"}
                      onChange={(e) => {
                        const updatedStep = { ...currentStep, approverType: e.target.value };
                        const updatedSteps = [...workflowSteps];
                        updatedSteps[editingStepIndex] = updatedStep;
                        setWorkflowSteps(updatedSteps);
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Reporting Manager</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`approverType_${editingStepIndex}`}
                      value="skip_if_self"
                      checked={currentStep.approverType === "skip_if_self"}
                      onChange={(e) => {
                        const updatedStep = { ...currentStep, approverType: e.target.value };
                        const updatedSteps = [...workflowSteps];
                        updatedSteps[editingStepIndex] = updatedStep;
                        setWorkflowSteps(updatedSteps);
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Skip if Self</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`approverType_${editingStepIndex}`}
                      value="individual"
                      checked={currentStep.approverType === "individual"}
                      onChange={(e) => {
                        const updatedStep = { ...currentStep, approverType: e.target.value };
                        const updatedSteps = [...workflowSteps];
                        updatedSteps[editingStepIndex] = updatedStep;
                        setWorkflowSteps(updatedSteps);
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Individual Employee</span>
                  </label>
                </div>
              </div>

              {currentStep.approverType === "role" && (
                <div>
                  <label className="block text-sm font-medium mb-1">Select Role *</label>
                  <select
                    value={currentStep.approverRole || ""}
                    onChange={(e) => {
                      const updatedStep = { ...currentStep, approverRole: e.target.value };
                      const updatedSteps = [...workflowSteps];
                      updatedSteps[editingStepIndex] = updatedStep;
                      setWorkflowSteps(updatedSteps);
                    }}
                    className="w-full px-3 py-2 border border-gray-300"
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.filter(r => r.type === "custom").map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {currentStep.approverType === "individual" && (
                <div>
                  <label className="block text-sm font-medium mb-1">Select Employee *</label>
                  <input
                    type="text"
                    value={currentStep.approverEmployee || ""}
                    onChange={(e) => {
                      const updatedStep = { ...currentStep, approverEmployee: e.target.value };
                      const updatedSteps = [...workflowSteps];
                      updatedSteps[editingStepIndex] = updatedStep;
                      setWorkflowSteps(updatedSteps);
                    }}
                    className="w-full px-3 py-2 border border-gray-300"
                    placeholder="Employee ID or Name"
                    required
                  />
                </div>
              )}

              {currentStep.stepType === "approval" && (
                <>
                  <div className="border-t border-gray-300 pt-4">
                    <h4 className="text-sm font-semibold mb-3">Step Actions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {["approve", "reject", "sendBack", "autoApprove"].map((action) => (
                        <label key={action} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={currentStep.actions?.[action] || false}
                            onChange={(e) => {
                              const updatedStep = {
                                ...currentStep,
                                actions: {
                                  ...currentStep.actions,
                                  [action]: e.target.checked,
                                },
                              };
                              const updatedSteps = [...workflowSteps];
                              updatedSteps[editingStepIndex] = updatedStep;
                              setWorkflowSteps(updatedSteps);
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm capitalize">{action === "sendBack" ? "Send Back" : action === "autoApprove" ? "Auto Approve" : action}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-300 pt-4">
                    <h4 className="text-sm font-semibold mb-3">SLA Configuration</h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentStep.sla?.enabled || false}
                          onChange={(e) => {
                            const updatedStep = {
                              ...currentStep,
                              sla: {
                                ...currentStep.sla,
                                enabled: e.target.checked,
                              },
                            };
                            const updatedSteps = [...workflowSteps];
                            updatedSteps[editingStepIndex] = updatedStep;
                            setWorkflowSteps(updatedSteps);
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Enable SLA</span>
                      </label>
                      {currentStep.sla?.enabled && (
                        <div>
                          <label className="block text-sm font-medium mb-1">SLA Hours</label>
                          <input
                            type="number"
                            value={currentStep.sla?.hours || 24}
                            onChange={(e) => {
                              const updatedStep = {
                                ...currentStep,
                                sla: {
                                  ...currentStep.sla,
                                  hours: parseInt(e.target.value) || 24,
                                },
                              };
                              const updatedSteps = [...workflowSteps];
                              updatedSteps[editingStepIndex] = updatedStep;
                              setWorkflowSteps(updatedSteps);
                            }}
                            className="w-full px-3 py-2 border border-gray-300"
                            min="1"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-300 pt-4">
                    <h4 className="text-sm font-semibold mb-3">Escalation</h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentStep.escalation?.enabled || false}
                          onChange={(e) => {
                            const updatedStep = {
                              ...currentStep,
                              escalation: {
                                ...currentStep.escalation,
                                enabled: e.target.checked,
                              },
                            };
                            const updatedSteps = [...workflowSteps];
                            updatedSteps[editingStepIndex] = updatedStep;
                            setWorkflowSteps(updatedSteps);
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Enable Escalation</span>
                      </label>
                      {currentStep.escalation?.enabled && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Escalate To</label>
                          <select
                            value={currentStep.escalation?.escalateTo || ""}
                            onChange={(e) => {
                              const updatedStep = {
                                ...currentStep,
                                escalation: {
                                  ...currentStep.escalation,
                                  escalateTo: e.target.value,
                                },
                              };
                              const updatedSteps = [...workflowSteps];
                              updatedSteps[editingStepIndex] = updatedStep;
                              setWorkflowSteps(updatedSteps);
                            }}
                            className="w-full px-3 py-2 border border-gray-300"
                          >
                            <option value="">Select Role/Employee</option>
                            {roles.filter(r => r.type === "custom").map((role) => (
                              <option key={role.id} value={`role_${role.id}`}>
                                Role: {role.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-300 pt-4">
                    <h4 className="text-sm font-semibold mb-3">Conditional Logic</h4>
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => {
                          const newCondition = {
                            field: "",
                            operator: "equals",
                            value: "",
                            logicalOperator: "AND",
                          };
                          const updatedStep = {
                            ...currentStep,
                            conditions: [...(currentStep.conditions || []), newCondition],
                          };
                          const updatedSteps = [...workflowSteps];
                          updatedSteps[editingStepIndex] = updatedStep;
                          setWorkflowSteps(updatedSteps);
                        }}
                        className="flex items-center space-x-1 rh-btn-primary text-sm px-3 py-1.5"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Condition</span>
                      </button>
                      {currentStep.conditions && currentStep.conditions.length > 0 && (
                        <div className="space-y-2">
                          {currentStep.conditions.map((condition, condIndex) => (
                            <div key={condIndex} className="border border-gray-300 p-3">
                              <div className="grid grid-cols-4 gap-2 items-end">
                                <div>
                                  <label className="block text-xs font-medium mb-1">Field</label>
                                  <select
                                    value={condition.field || ""}
                                    onChange={(e) => {
                                      const updatedConditions = [...currentStep.conditions];
                                      updatedConditions[condIndex] = { ...condition, field: e.target.value };
                                      const updatedStep = { ...currentStep, conditions: updatedConditions };
                                      const updatedSteps = [...workflowSteps];
                                      updatedSteps[editingStepIndex] = updatedStep;
                                      setWorkflowSteps(updatedSteps);
                                    }}
                                    className="w-full px-2 py-1 border border-gray-300 text-sm"
                                  >
                                    <option value="">Select Field</option>
                                    <optgroup label="Employee Attributes">
                                      <option value="grade">Grade</option>
                                      <option value="department">Department</option>
                                      <option value="location">Location</option>
                                      <option value="employmentType">Employment Type</option>
                                      <option value="role">Role</option>
                                    </optgroup>
                                    <optgroup label="Transaction Values">
                                      <option value="amount">Amount</option>
                                      <option value="days">Days</option>
                                      <option value="percentage">Percentage</option>
                                    </optgroup>
                                    <optgroup label="Master Data">
                                      <option value="branch">Branch</option>
                                      <option value="department_master">Department (Master)</option>
                                    </optgroup>
                                    <optgroup label="Previous Step">
                                      <option value="previous_step_outcome">Previous Step Outcome</option>
                                    </optgroup>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium mb-1">Operator</label>
                                  <select
                                    value={condition.operator || "equals"}
                                    onChange={(e) => {
                                      const updatedConditions = [...currentStep.conditions];
                                      updatedConditions[condIndex] = { ...condition, operator: e.target.value };
                                      const updatedStep = { ...currentStep, conditions: updatedConditions };
                                      const updatedSteps = [...workflowSteps];
                                      updatedSteps[editingStepIndex] = updatedStep;
                                      setWorkflowSteps(updatedSteps);
                                    }}
                                    className="w-full px-2 py-1 border border-gray-300 text-sm"
                                  >
                                    <option value="equals">Equals</option>
                                    <option value="not_equals">Not Equals</option>
                                    <option value="greater_than">Greater Than</option>
                                    <option value="less_than">Less Than</option>
                                    <option value="greater_equal">Greater or Equal</option>
                                    <option value="less_equal">Less or Equal</option>
                                    <option value="contains">Contains</option>
                                    <option value="in">In (List)</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium mb-1">Value</label>
                                  <input
                                    type="text"
                                    value={condition.value || ""}
                                    onChange={(e) => {
                                      const updatedConditions = [...currentStep.conditions];
                                      updatedConditions[condIndex] = { ...condition, value: e.target.value };
                                      const updatedStep = { ...currentStep, conditions: updatedConditions };
                                      const updatedSteps = [...workflowSteps];
                                      updatedSteps[editingStepIndex] = updatedStep;
                                      setWorkflowSteps(updatedSteps);
                                    }}
                                    className="w-full px-2 py-1 border border-gray-300 text-sm"
                                    placeholder="Enter value"
                                  />
                                </div>
                                <div className="flex items-center space-x-1">
                                  {condIndex > 0 && (
                                    <select
                                      value={condition.logicalOperator || "AND"}
                                      onChange={(e) => {
                                        const updatedConditions = [...currentStep.conditions];
                                        updatedConditions[condIndex] = { ...condition, logicalOperator: e.target.value };
                                        const updatedStep = { ...currentStep, conditions: updatedConditions };
                                        const updatedSteps = [...workflowSteps];
                                        updatedSteps[editingStepIndex] = updatedStep;
                                        setWorkflowSteps(updatedSteps);
                                      }}
                                      className="px-2 py-1 border border-gray-300 text-sm"
                                    >
                                      <option value="AND">AND</option>
                                      <option value="OR">OR</option>
                                    </select>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updatedConditions = currentStep.conditions.filter((_, i) => i !== condIndex);
                                      const updatedStep = { ...currentStep, conditions: updatedConditions };
                                      const updatedSteps = [...workflowSteps];
                                      updatedSteps[editingStepIndex] = updatedStep;
                                      setWorkflowSteps(updatedSteps);
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {(!currentStep.conditions || currentStep.conditions.length === 0) && (
                        <p className="text-xs text-gray-500 text-center py-2 border border-gray-200">
                          No conditions. Step will execute for all cases. Add conditions to make step conditional.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-300 pt-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentStep.parallelApproval || false}
                        onChange={(e) => {
                          const updatedStep = { ...currentStep, parallelApproval: e.target.checked };
                          const updatedSteps = [...workflowSteps];
                          updatedSteps[editingStepIndex] = updatedStep;
                          setWorkflowSteps(updatedSteps);
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Enable Parallel Approval (if multiple approvers)</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 p-4 border-t border-gray-300 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                setShowStepConfig(false);
                setEditingStepIndex(null);
              }}
              className="bg-gray-500 text-white px-4 py-2 hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                handleSaveStep(currentStep);
              }}
              className="bg-green-600 text-white px-4 py-2 hover:bg-green-700"
            >
              Save Step
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex justify-between items-center p-4 border-b border-gray-300 flex-shrink-0">
        <h3 className="text-lg font-semibold">Workflow Management</h3>
        <button
          onClick={() => {
            resetWorkflowWizard();
            setShowWorkflowForm(true);
          }}
          className="flex items-center space-x-2 rh-btn-primary px-4 py-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Workflow</span>
        </button>
      </div>

      <div className="flex-1 p-4 overflow-hidden flex flex-col">
        <div className="mb-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchWorkflows}
              onChange={(e) => setSearchWorkflows(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
            />
          </div>
        </div>

        {workflows.filter(workflow => 
          !searchWorkflows || 
          workflow.name?.toLowerCase().includes(searchWorkflows.toLowerCase()) ||
          workflow.moduleName?.toLowerCase().includes(searchWorkflows.toLowerCase()) ||
          workflow.category?.toLowerCase().includes(searchWorkflows.toLowerCase())
        ).length === 0 ? (
          <p className="text-gray-500 text-center py-4">No workflows found</p>
        ) : (
          <div className="h-full overflow-y-auto flex-1">
            <div className="space-y-2">
              {workflows.filter(workflow => 
                !searchWorkflows || 
                workflow.name?.toLowerCase().includes(searchWorkflows.toLowerCase()) ||
                workflow.moduleName?.toLowerCase().includes(searchWorkflows.toLowerCase()) ||
                workflow.category?.toLowerCase().includes(searchWorkflows.toLowerCase())
              ).map((workflow) => (
                <div
                  key={workflow.id}
                  className="bg-white p-4 border border-gray-300 flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm">{workflow.name}</h4>
                      <span className="px-2 py-0.5 rh-accent-soft text-[10px] font-medium">
                        {workflow.moduleName}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-[10px] font-medium">
                        {workflow.category}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] font-medium ${
                        workflow.active 
                          ? "bg-green-100 text-green-800" 
                          : "bg-gray-100 text-gray-800"
                      }`}>
                        {workflow.active ? "Active" : "Inactive"}
                      </span>
                      {workflow.steps && workflow.steps.length > 0 && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-medium">
                          {workflow.steps.length} Step{workflow.steps.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      <p>Code: {workflow.code || "N/A"}</p>
                      <p>Trigger: {workflow.triggerEvent || "N/A"}</p>
                      <p>Scope: {workflow.applicableEmployeeScope === "all" ? "All Employees" : "Filtered"}</p>
                    </div>
                  </div>
                  <div className="relative" ref={openWorkflowMenuId === workflow.id ? workflowMenuRef : null}>
                    <button
                      onClick={() => setOpenWorkflowMenuId(openWorkflowMenuId === workflow.id ? null : workflow.id)}
                      className="text-gray-600 hover:text-gray-800 p-1"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    {openWorkflowMenuId === workflow.id && (
                      <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-300 shadow-lg z-50">
                        <button
                          onClick={() => {
                            handleEditWorkflow(workflow);
                            setOpenWorkflowMenuId(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => {
                            handleDeleteWorkflow(workflow.id);
                            setOpenWorkflowMenuId(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Workflow Form Wizard Modal */}
      {showWorkflowForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[60]">
          <div className="bg-white shadow-xl relative w-[1000px] h-[700px] flex flex-col border border-gray-300">
            <div className="flex items-center justify-between p-4 border-b border-gray-300 flex-shrink-0">
              <div className="flex items-center space-x-4">
                <h3 className="text-xl font-bold">{editingWorkflow ? "Edit Workflow" : "Add Workflow"}</h3>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs ${
                        wizardStep === step
                          ? 'border-redhalo-red bg-redhalo-mint/50 text-redhalo-red'
                          : wizardStep > step
                          ? 'border-green-600 bg-green-50 text-green-600'
                          : 'border-gray-300 bg-gray-50 text-gray-400'
                      }`}>
                        {wizardStep > step ? <CheckSquare className="w-4 h-4" /> : step}
                      </div>
                      {step < 4 && (
                        <div className={`w-8 h-0.5 ${wizardStep > step ? 'bg-green-600' : 'bg-gray-300'}`}></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={resetWorkflowWizard}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
              <form onSubmit={handleWorkflowSubmit} id="workflow-form">
                {wizardStep === 1 && renderWizardStep1()}
                {wizardStep === 2 && renderWizardStep2()}
                {wizardStep === 3 && renderWizardStep3()}
                {wizardStep === 4 && renderWizardStep4()}
              </form>
            </div>

            <div className="flex justify-between items-center p-4 border-t border-gray-300 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  if (wizardStep > 1) {
                    setWizardStep(wizardStep - 1);
                  } else {
                    resetWorkflowWizard();
                  }
                }}
                className="border-2 border-gray-500 text-gray-700 px-4 py-2 hover:border-gray-600 hover:text-gray-900"
              >
                {wizardStep === 1 ? "Cancel" : "Previous"}
              </button>
              <button
                type="submit"
                form="workflow-form"
                className="border-2 border-redhalo-red text-redhalo-navy px-4 py-2 hover:border-redhalo-red-dark hover:text-redhalo-navy-dark"
              >
                {wizardStep === 4 ? (editingWorkflow ? "Update" : "Save") : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step Configuration Modal */}
      {renderWorkflowStepConfig()}

      {/* Filters Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-[70]">
          <Filters
            onApply={handleFilterApply}
            onClose={handleFilterClose}
            darkMode={false}
            initialFilters={workflowFormData.filterData?.filters || {}}
          />
        </div>
      )}
    </div>
  );
}

